/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-anonymous-default-export */
import React, { useCallback, useRef, useState } from 'react'
import { Input, Button, Icon, FormPro } from '@roo/roo'
import Modal from '@roo/roo/Modal'
import PopConfirm from '@roo/roo/PopConfirm'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import {
  fetchCostCenterList,
  createCostCenter,
  updateCostCenter,
  deleteCostCenter,
  ICostCenterItem,
} from './api'
import { failToast, successToast } from '@/common/utils/toast'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import styles from './index.module.less'

type ModalType = 'create' | 'edit'

interface CostCenterModalState {
  visible: boolean;
  type: ModalType;
  record?: ICostCenterItem;
}

type CreateRow = {
  key: string;
  name: string;
  description: string;
  nameError?: string;
}

const CostCenterPage = () => {
  const { t } = useTranslation()
  const [inputVal, setInputVal] = useState('')
  const [query, setQuery] = useState({ keyword: '', pageNo: 1, pageSize: 10 })
  const { keyword, pageNo, pageSize } = query
  const [modal, setModal] = useState<CostCenterModalState>({ visible: false, type: 'create' })
  const [form] = FormPro.useForm()
  const [createRows, setCreateRows] = useState<CreateRow[]>([{ key: `row-${Date.now()}`, name: '', description: '' }])

  const NAME_MAX_LENGTH = 64

  const handleRowNameChange = useCallback((index: number, value: string) => {
    const lengthError = value.length > NAME_MAX_LENGTH ? t('e_pc_cost_center_length_limit', '成本中心超出长度限制（最多 64 个字符）') : undefined
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, name: value, nameError: lengthError } : row))
  }, [t])

  const handleRowNameBlur = useCallback(() => {
    setCreateRows(prev => {
      const nameCount: Record<string, number> = {}
      prev.forEach(row => {
        const name = row.name.trim()
        if (name) nameCount[name] = (nameCount[name] || 0) + 1
      })
      return prev.map(row => {
        const name = row.name.trim()
        const lenKey = t('e_pc_cost_center_length_limit', '成本中心超出长度限制（最多 64 个字符）')
        const dupKey = t('e_pc_cost_center_duplicate_hint', '存在重复成本中心，请调整')
        if (row.name.length > NAME_MAX_LENGTH) return { ...row, nameError: lenKey }
        if (name && nameCount[name] > 1) return { ...row, nameError: dupKey }
        if (row.nameError === dupKey) return { ...row, nameError: undefined }
        return row
      })
    })
  }, [t])

  const handleRowDescChange = useCallback((index: number, value: string) => {
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, description: value } : row))
  }, [])

  const handleRowAdd = useCallback(() => {
    setCreateRows(prev => [...prev, { key: `row-${Date.now()}`, name: '', description: '' }])
  }, [])

  const handleRowRemove = useCallback((index: number) => {
    setCreateRows(prev => {
      const filtered = prev.filter((_, i) => i !== index)
      const nameCount: Record<string, number> = {}
      filtered.forEach(row => {
        const name = row.name.trim()
        if (name) nameCount[name] = (nameCount[name] || 0) + 1
      })
      const dupKey = t('e_pc_cost_center_duplicate_hint', '存在重复成本中心，请调整')
      return filtered.map(row => {
        const name = row.name.trim()
        if (name && nameCount[name] > 1) return { ...row, nameError: dupKey }
        if (row.nameError === dupKey) return { ...row, nameError: undefined }
        return row
      })
    })
  }, [t])

  const { data: listRes, loading, run: fetchList } = useRequest(
    () => fetchCostCenterList({ keyword: keyword || undefined, pageNo, pageSize }).then(res => res.data),
    { manual: true }
  )

  const listData = listRes?.result || []
  const totalCount = listRes?.totalCount || 0

  React.useEffect(() => {
    fetchList()
  }, [query])

  const handleSearch = () => {
    setQuery(prev => ({ ...prev, keyword: inputVal, pageNo: 1 }))
  }

  const refreshList = () => fetchList()

  const handleOpenCreate = () => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', description: '' }])
    setModal({ visible: true, type: 'create' })
  }

  const handleOpenEdit = (record: ICostCenterItem) => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: record.name, description: record.description || '' }])
    setModal({ visible: true, type: 'edit', record })
  }

  const handleModalClose = () => {
    setModal(prev => ({ ...prev, visible: false }))
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', description: '' }])
  }

  const { run: handleModalSubmit, loading: submitting } = useRequest(
    async () => {
      const { type, record } = modal
      if (type === 'edit' && record) {
        const row = createRows[0]
        if (!row.name.trim()) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, nameError: t('e_pc_cost_center_placeholder', '请输入成本中心') } : r))
          return
        }
        if (row.name.length > NAME_MAX_LENGTH) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, nameError: t('e_pc_cost_center_length_limit', '成本中心超出长度限制（最多 64 个字符）') } : r))
          return
        }
        await updateCostCenter({
          id: record.id,
          name: row.name.trim(),
          description: row.description?.trim() || undefined,
        })
        successToast(t('e_pc_update_success', '修改成功'))
        handleModalClose()
        refreshList()
      } else {
        let hasError = false
        const validated = createRows.map(row => {
          if (!row.name.trim()) {
            hasError = true
            return { ...row, nameError: t('e_pc_cost_center_placeholder', '请输入成本中心') }
          }
          if (row.name.length > NAME_MAX_LENGTH) {
            hasError = true
            return { ...row, nameError: t('e_pc_cost_center_length_limit', '成本中心超出长度限制（最多 64 个字符）') }
          }
          return row
        })
        if (hasError) {
          setCreateRows(validated)
          return
        }
        const dupKey = t('e_pc_cost_center_duplicate_hint', '存在重复成本中心，请调整')
        const duplicateValidated = validated.map((row, i) => {
          const firstOccurrenceIndex = validated.findIndex(r => r.name.trim() === row.name.trim())
          return firstOccurrenceIndex !== i ? { ...row, nameError: dupKey } : row
        })
        if (duplicateValidated.some(row => row.nameError === dupKey)) {
          setCreateRows(duplicateValidated)
          return
        }
        // 批量创建
        const res = await createCostCenter({
          costCenterCreateList: createRows.map(row => ({
            name: row.name.trim(),
            description: row.description?.trim() || undefined,
          })),
        })
        // 处理后端返回的重复冲突列表
        if (res?.conflictList && res.conflictList.length > 0) {
          const conflictNames = new Set(res.conflictList.map(c => c.name))
          const dupKey = t('e_pc_cost_center_duplicate', '该成本中心已存在，请更换后重试')
          setCreateRows(prev => prev.map(row =>
            conflictNames.has(row.name.trim()) ? { ...row, nameError: dupKey } : row
          ))
          return
        }
        successToast(t('e_pc_create_success', '创建成功'))
        handleModalClose()
        refreshList()
      }
    },
    { manual: true }
  )

  const { run: handleConfirmDelete, loading: deleting } = useRequest(
    async (record: ICostCenterItem) => {
      await deleteCostCenter({ id: record.id })
      successToast(t('e_pc_del_success', '删除成功'))
      refreshList()
    },
    { manual: true }
  )

  const handlePageChange = (_pageNo: number, _pageSize: number) => {
    setQuery(prev => ({ ...prev, pageNo: _pageNo, pageSize: _pageSize }))
  }

  const isEdit = modal.type === 'edit'

  const columns = [
    {
      dataIndex: 'name',
      title: t('e_pc_cost_center_tab', '成本中心'),
      render: (text: string) => <TipText text={text} />,
    },
    {
      dataIndex: 'description',
      title: t('e_pc_description', '描述'),
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      dataIndex: 'staffCount',
      title: t('e_pc_staff_count', '员工数量'),
      width: 160,
    },
    {
      dataIndex: '',
      title: t('batch_common_operation', '操作'),
      width: 220,
      fixed: 'right',
      render: (_: never, record: ICostCenterItem) => (
        <div>
          <Button type="brand-text" onClick={() => handleOpenEdit(record)}>
            {t('e_pc_edit', '编辑')}
          </Button>&ensp;&ensp;
          <PopConfirm
            content={t('e_pc_confirm_delete_cost_center', '确认删除此成本中心？')}
            placement="left"
            onConfirm={() => handleConfirmDelete(record)}
            cancelText={t('e_pc_cancel', '取消')}
            confirmText={t('e_pc_submit', '确认')}
            disabled={deleting || record.staffCount > 0}
          >
            <Button
              type="brand-text"
              disabled={deleting}
              onClick={() => {
                if (record.staffCount > 0) {
                  failToast(t('e_pc_cost_center_cannot_delete', '此成本中心包含员工，无法删除'))
                }
              }}
            >
              {t('e_pc_del', '删除')}
            </Button>
          </PopConfirm>
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }} style={{ paddingBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px', alignItems: 'end' }}>
          <FormPro.Item label={t('e_pc_cost_center_tab', '成本中心')} style={{ marginBottom: 0 }}>
            <Input
              allowClear
              placeholder={t('e_pc_cost_center_placeholder', '请输入成本中心')}
              value={inputVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputVal(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleSearch()
              }}
            />
          </FormPro.Item>
          <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button type="brand" size="normal" htmlType="button" onClick={handleSearch} className="btn-dark">
              {t('e_pc_aquire', '查询')}
            </Button>
          </div>
        </div>
      </FormPro>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="brand" size="normal" htmlType="button" onClick={handleOpenCreate}>
          {t('e_pc_add_cost_center', '新增成本中心')}
        </Button>
      </div>

      <CommonTable
        rowKey="id"
        columns={columns}
        data={listData}
        loading={loading}
        pagination={{
          total: totalCount,
          currentPage: pageNo,
          pageSize,
          onChange: handlePageChange,
        }}
      />

      {/* 新增/编辑 弹窗 */}
      <Modal
        title={isEdit ? t('e_pc_cost_center_edit_popup', '编辑成本中心') : t('e_pc_add_cost_center', '新增成本中心')}
        visible={modal.visible}
        onCancel={handleModalClose}
        showCancelButton={false}
        showConfirmButton={false}
        closeMotion={false}
        backdropClosable={false}
        style={{ width: '680px' }}
      >
        <FormPro
          form={form}
          colon={false}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          onFinish={handleModalSubmit}
        >
          <div className={styles.modalFormList}>
            {createRows.map((row, index) => (
              <div key={row.key} className={`${styles.formListItem} ${isEdit ? styles.formListItemNoIndex : ''}`}>
                {!isEdit && <span className={styles.formListIndex}>{index + 1}</span>}
                <div className={styles.formListFields}>
                  <div className={styles.formListFieldsRow}>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>
                        <span className={styles.formListColRequired}>*&nbsp;</span>
                        {t('e_pc_cost_center_tab', '成本中心')}
                      </div>
                      <Input
                        value={row.name}
                        status={row.nameError ? 'error' : undefined}
                        placeholder={t('e_pc_cost_center_placeholder', '请输入成本中心')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowNameChange(index, e.target.value)}
                        onBlur={handleRowNameBlur}
                        style={{ width: '100%' }}
                      />
                      {row.nameError && <div className={styles.formListColError}>{row.nameError}</div>}
                    </div>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>{t('e_pc_description', '描述')}</div>
                      <Input
                        value={row.description}
                        placeholder={t('e_pc_description_placeholder', '请输入描述')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowDescChange(index, e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                {!isEdit && createRows.length > 1 && (
                  <Button type="brand-text" onClick={() => handleRowRemove(index)} className={styles.formListDeleteBtn}>
                    <Icon name="trash-o" size={20} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {/* 按钮动态处理 */}
          {!isEdit && (
            <div className={styles.addRowBtnWrap}>
              <Button className={styles.addRowBtn} type="brand-text" onClick={handleRowAdd}>
                + {t('e_pc_add_cost_center', '新增成本中心')}
              </Button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px', gap: '12px' }}>
            <Button type="hollow" onClick={handleModalClose}>
              {t('e_pc_cancel_button', '取消')}
            </Button>
            <Button type="brand" onClick={() => handleModalSubmit()} loading={submitting}>
              {t('e_pc_confirm_button', '确认')}
            </Button>
          </div>
        </FormPro>
      </Modal>
    </div>
  )
}

export default CostCenterPage
